import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { House, FileText, ChatCircleDots, Stack, Lightning, NotePencil, Gear, SignOut, ChartBar, Target, Moon, DotsThreeOutline, X } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

import Overview from "@/pages/dashboard/Overview";
import MyProfile from "@/pages/dashboard/MyProfile";
import Reports from "@/pages/dashboard/Reports";
import Coach from "@/pages/dashboard/Coach";
import AssessmentsHub from "@/pages/dashboard/AssessmentsHub";
import InnerPractices from "@/pages/dashboard/InnerPractices";
import Journal from "@/pages/dashboard/Journal";
import Symbolic from "@/pages/dashboard/Symbolic";
import Settings from "@/pages/dashboard/Settings";

const NAV = [
  { id: "home", label: "Home", icon: House },
  { id: "profile", label: "My Profile", icon: Target },
  { id: "reports", label: "My Reports", icon: FileText },
  { id: "coach", label: "AI Coach", icon: ChatCircleDots },
  { id: "assessments", label: "Assessments", icon: Stack },
  { id: "practices", label: "Inner Practices", icon: Lightning },
  { id: "journal", label: "Journal", icon: NotePencil },
  { id: "symbolic", label: "Symbolic", icon: Moon },
  { id: "settings", label: "Settings", icon: Gear },
];

// Primary tabs shown in the mobile bottom bar (rest live under "More").
const PRIMARY_TABS = ["home", "profile", "coach", "assessments"];
const MORE_IDS = ["reports", "practices", "journal", "symbolic", "settings"];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("home");
  const [moreOpen, setMoreOpen] = useState(false);

  const go = (id) => { setActive(id); setMoreOpen(false); window.scrollTo(0, 0); };
  const handleLogout = async () => { await logout(); navigate("/"); };

  const primaryTabs = NAV.filter((n) => PRIMARY_TABS.includes(n.id));
  const moreItems = NAV.filter((n) => MORE_IDS.includes(n.id));
  const moreActive = MORE_IDS.includes(active);

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Slim mobile top bar */}
      <div className="safe-top sticky top-0 z-40 flex items-center justify-between border-b border-slate-800/60 bg-slate-950/80 px-5 py-3 backdrop-blur-xl lg:hidden">
        <Logo />
        {user?.role === "admin" && (
          <button onClick={() => navigate("/admin")} className="flex items-center gap-1.5 rounded-full border border-amber-500/30 px-3 py-1.5 text-xs font-medium text-amber-300" data-testid="mobile-admin-btn"><ChartBar size={14} weight="duotone" /> Admin</button>
        )}
      </div>

      <div className="mx-auto flex max-w-[1400px]">
        {/* Desktop sidebar */}
        <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-slate-800/60 bg-slate-950 px-4 py-6 lg:static lg:block">
          <div className="px-2"><Logo /></div>
          <nav className="mt-8 space-y-1">
            {NAV.map((n) => {
              const isActive = active === n.id;
              return (
                <button key={n.id} onClick={() => go(n.id)} className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${isActive ? "bg-teal-500/15 text-teal-300" : "text-slate-400 hover:bg-slate-900 hover:text-slate-200"}`} data-testid={`nav-${n.id}`}>
                  <n.icon size={18} weight={isActive ? "fill" : "duotone"} /> {n.label}
                </button>
              );
            })}
          </nav>
          {user?.role === "admin" && (
            <button onClick={() => navigate("/admin")} className="mt-4 flex w-full items-center gap-3 rounded-lg border border-amber-500/30 px-3 py-2.5 text-sm font-medium text-amber-300 transition-all hover:bg-amber-500/10" data-testid="nav-admin">
              <ChartBar size={18} weight="duotone" /> Admin Panel
            </button>
          )}
          <div className="absolute bottom-6 left-4 right-4">
            <div className="rounded-lg border border-slate-800 bg-slate-900 p-3">
              <p className="truncate text-sm font-medium text-slate-200">{user?.name}</p>
              <p className="truncate text-xs text-slate-500">{user?.email}</p>
              <button onClick={handleLogout} className="mt-2.5 flex items-center gap-1.5 text-xs text-slate-500 hover:text-red-400" data-testid="dash-logout-btn"><SignOut size={14} /> Sign out</button>
            </div>
          </div>
        </aside>

        <main className="has-bottom-nav min-h-screen flex-1 px-5 py-6 sm:px-8 lg:px-12 lg:py-8 lg:pb-8">
          {active === "home" && <Overview onNavigate={go} />}
          {active === "profile" && <MyProfile onNavigate={go} />}
          {active === "reports" && <Reports onNavigate={go} />}
          {active === "coach" && <Coach />}
          {active === "assessments" && <AssessmentsHub />}
          {active === "practices" && <InnerPractices onNavigate={go} />}
          {active === "journal" && <Journal />}
          {active === "symbolic" && <Symbolic />}
          {active === "settings" && <Settings />}
        </main>
      </div>

      {/* Mobile bottom tab bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-800/70 bg-slate-950/90 backdrop-blur-xl lg:hidden" data-testid="mobile-tabbar">
        <div className="mx-auto grid max-w-lg grid-cols-5 pb-[env(safe-area-inset-bottom)]">
          {primaryTabs.map((n) => {
            const isActive = active === n.id;
            return (
              <button key={n.id} onClick={() => go(n.id)} className="relative flex min-h-[60px] flex-col items-center justify-center gap-1 py-2 transition-colors active:bg-slate-900/60" data-testid={`tab-${n.id}`}>
                {isActive && <span className="absolute top-0 h-0.5 w-8 rounded-full bg-teal-400" />}
                <n.icon size={23} weight={isActive ? "fill" : "regular"} className={isActive ? "text-teal-400" : "text-slate-500"} />
                <span className={`text-[10px] font-medium ${isActive ? "text-teal-300" : "text-slate-500"}`}>{n.label === "My Profile" ? "Profile" : n.label === "Assessments" ? "Tests" : n.label === "AI Coach" ? "Coach" : n.label}</span>
              </button>
            );
          })}
          <button onClick={() => setMoreOpen(true)} className="relative flex min-h-[60px] flex-col items-center justify-center gap-1 py-2 transition-colors active:bg-slate-900/60" data-testid="tab-more">
            {moreActive && <span className="absolute top-0 h-0.5 w-8 rounded-full bg-teal-400" />}
            <DotsThreeOutline size={23} weight={moreActive ? "fill" : "regular"} className={moreActive ? "text-teal-400" : "text-slate-500"} />
            <span className={`text-[10px] font-medium ${moreActive ? "text-teal-300" : "text-slate-500"}`}>More</span>
          </button>
        </div>
      </nav>

      {/* More bottom sheet */}
      {moreOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="more-sheet">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMoreOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 animate-[slideUp_.25s_ease-out] rounded-t-3xl border-t border-slate-800 bg-slate-900 pb-[env(safe-area-inset-bottom)]">
            <div className="flex items-center justify-between px-6 pb-2 pt-4">
              <div className="mx-auto h-1 w-10 rounded-full bg-slate-700" />
            </div>
            <div className="flex items-center justify-between px-6 pb-1">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">More</p>
              <button onClick={() => setMoreOpen(false)} className="text-slate-400" data-testid="more-close"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-3 gap-3 px-6 py-4">
              {moreItems.map((n) => {
                const isActive = active === n.id;
                return (
                  <button key={n.id} onClick={() => go(n.id)} className={`flex flex-col items-center gap-2 rounded-2xl border px-2 py-4 transition-all ${isActive ? "border-teal-400/50 bg-teal-500/10" : "border-slate-800 bg-slate-950 active:bg-slate-800"}`} data-testid={`more-${n.id}`}>
                    <n.icon size={24} weight="duotone" className={isActive ? "text-teal-400" : "text-slate-300"} />
                    <span className={`text-xs font-medium ${isActive ? "text-teal-300" : "text-slate-300"}`}>{n.label}</span>
                  </button>
                );
              })}
            </div>
            <div className="border-t border-slate-800 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-200">{user?.name}</p>
                  <p className="truncate text-xs text-slate-500">{user?.email}</p>
                </div>
                <button onClick={handleLogout} className="flex shrink-0 items-center gap-1.5 rounded-full border border-slate-700 px-4 py-2 text-xs font-medium text-slate-300 active:bg-slate-800" data-testid="more-logout-btn"><SignOut size={14} /> Sign out</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
